import { SubmitActions, SubmitData, UIElement } from '@adyen/adyen-web/auto'
import { AdyenPaymentMethodsType } from '../../api/qrMenu/types'
import { useMySelector } from '../useMySelector'
import '@adyen/adyen-web/styles/adyen.css'
import { useOpenModal } from '../useOpenModal'
import { getPaymentMethodsConfig } from './getPaymentConfig'
import { useMyDispatch } from '../useMyDispatch'
import { ModalsSlice } from 'featureSliced/shared/store/reducers/ModalsReducer'
import { adyenModalId, openAdyenModal } from './openAdyenModal'
import { useOpenAdyenRetry } from './useOpenAdyenRetry'
import { qrMenuPOST } from 'featureSliced/shared/api/qrMenu/POST'
import { useRef } from 'react'
import { ReceiptModal } from 'components/ReceiptModal'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { useNavigate } from 'react-router-dom'
import { clickCollectPOST } from 'featureSliced/shared/api/clickCollect/POST'
import { ToastsSlice } from 'featureSliced/shared/store/reducers/ToasterReducer'
import { qrMenuGET } from 'featureSliced/shared/api/qrMenu/GET'

const ADYEN_LOCALES: Record<string, string> = {
  en: 'en-US',
  de: 'de-DE',
  it: 'it-IT',
  fr: 'fr-FR',
}

export const useAdyen = () => {
  // Hooks
  const dispatch = useMyDispatch()
  const openModal = useOpenModal()
  const openAdyenRetry = useOpenAdyenRetry()

  // Store
  const closeModal = ModalsSlice.actions.closeModal
  const userLang = useMySelector((state) => state.common.selectedLanguage)
  const country = useMySelector((state) => state.app.address.Country)
  const currency = useMySelector((state) => state.app.companyData.Currency)
  const adyenShopId = useMySelector(
    (state) => state.app.paymentConfig?.hsPay?.shopId
  )

  const paymentKeyRef = useRef<string | null>(null)

  const startAdyenPayment = async (
    value = 1000,
    // TODO: add types
    createPayment: (hsPaymentData: any) => any,
    completePayment: (transactionId: string | null) => void,
    isPaymentMethodUpdate = false
  ) => {
    try {
      if (adyenShopId) {
        const paymentMethods = await qrMenuGET.adyenPaymentMethods(adyenShopId)

        // Remove storedPaymentMethods if isPaymentMethodUpdate is true
        const paymentMethodsResponse = isPaymentMethodUpdate
          ? (() => {
              const { storedPaymentMethods, ...rest } = paymentMethods
              return rest
            })()
          : paymentMethods

        // const environment: 'test' | 'live'
        const environment =
          process.env.REACT_APP_IS_PROD === '0'
            ? ('test' as const)
            : ('live' as const)
        const configuration = {
          paymentMethodsResponse,
          clientKey: paymentMethods?.clientKey || '',
          environment,
          amount: {
            value,
            currency,
          },
          locale: ADYEN_LOCALES[userLang],
          // shopperLocale: ADYEN_LOCALES[userLang],
          showPayButton: true,
          countryCode: country === '1' ? 'CH' : 'DE',
          onSubmit: async (
            state: SubmitData,
            component: UIElement,
            actions: SubmitActions
          ) => {
            try {
              if (state.isValid) {
                const res = await createPayment({
                  ...state.data,
                  amount: value,
                  currency,
                })
                const { resultCode, action = {}, paymentKey } = res

                if (isPaymentMethodUpdate) {
                  paymentKeyRef.current = res?.transactionId ?? null
                  actions.resolve({
                    resultCode: res.resultCode as any,
                  })
                } else if (!resultCode || resultCode === 'Error') {
                  console.log('payment rejected')
                  paymentKeyRef.current = null
                  // TODO: show modal
                  actions.reject()
                } else {
                  paymentKeyRef.current = res?.paymentKey ?? null
                  // actions.resolve({
                  //   resultCode,
                  // })
                  actions.resolve({
                    resultCode: res.resultCode as any,
                    ...(res.action ? { action: res.action } : {}),
                    ...(res.order ? { order: res.order } : {}),
                  })
                }
              } else {
                throw new Error('No adyenShopId')
              }
            } catch (error) {
              paymentKeyRef.current = null
              console.error(error)
              actions.reject()
            }
          },

          onPaymentCompleted: async (result: any, component: any) => {
            completePayment(paymentKeyRef.current || null)
            dispatch(closeModal(adyenModalId))
            paymentKeyRef.current = null
          },

          onPaymentFailed: (result: any, component: any) => {
            console.log('FAIL', result)
            console.log('component', component)

            dispatch(closeModal(adyenModalId))
            openAdyenRetry(() =>
              openAdyenModal(openModal, configuration, paymentMethodsConfig)
            )
          },

          onError: (error: any, component: any) => {
            console.log('ERROR:', error)
            console.log('component', component)

            dispatch(closeModal(adyenModalId))
            openAdyenRetry(() =>
              openAdyenModal(openModal, configuration, paymentMethodsConfig)
            )
          },

          // Used for the Native 3DS2 Authentication flow, see: https://docs.adyen.com/online-payments/3d-secure/native-3ds2/
          onAdditionalDetails: async (
            state: any,
            component: any,
            actions: any
          ) => {
            console.info('onAdditionalDetails', state, component)
            //   try {
            //     const { resultCode } = await fetch('/api/payments/details', {
            //       method: 'POST',
            //       body: state.data ? JSON.stringify(state.data) : '',
            //       headers: {
            //         'Content-Type': 'application/json',
            //       },
            //     }).then((response) => response.json())

            //     if (!resultCode) {
            //       console.warn('reject')
            //       actions.reject()
            //     }

            //     actions.resolve({ resultCode })
            //   } catch (error) {
            //     console.error(error)
            //     actions.reject()
            //   }
          },
        }

        const paymentMethodsConfig = getPaymentMethodsConfig(value, currency)
        openAdyenModal(openModal, configuration, paymentMethodsConfig)
      }
    } catch (error) {
      console.error(error)
      console.log('Error occurred. Look at console for details.')
    }
  }

  return startAdyenPayment
}
